<template>
  <article class="order-history">
    <h3 class="order-history__heading" @click="setOpen(!localOpen)">
      {{ $t("headings.orderHistory") }}
      <lucide-icon :name="localOpen ? 'chevron-up' : 'chevron-down'" />
    </h3>
    <p v-if="localOpen" class="message message-info">
      {{ $t("messages.orderHistoryInfo") }}
    </p>
    <table
      class="order-history__items"
      v-if="localOpen && orderHistory.length > 0"
    >
      <thead>
        <tr class="order-history__header">
          <th>{{ $t("labels.orderDate") }}</th>
          <th>{{ $t("labels.type") }}</th>
          <th>{{ $t("labels.price") }}</th>
          <th>{{ $t("labels.article") }}</th>
          <td>{{ $t("words.invoice") }}</td>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="item in orderHistory"
          :key="item.created"
          class="order-history__item"
        >
          <td :title="formatDateStringRelative(item.created) ?? ''">
            {{ formatDateTime(item.created) }}
          </td>
          <td>{{ $t(`ordertypes.${item.type}`) }}</td>
          <td>{{ item.price }} {{ item.priceCurrency }}</td>
          <td>
            <div v-if="item.itemType">
              {{ plans?.[item.itemType]?.label ?? item.itemType
              }}<span v-if="item.licenses > 0"
                >, {{ $t("labels.countUsers", item.licenses) }}</span
              ><span v-if="item.teams > 0"
                >, {{ $t("labels.xTeams", item.teams) }}</span
              >
            </div>
            <div v-for="upgrade in item.upgrades" :key="upgrade">
              {{
                $te(`upgrades.${upgrade}.name`)
                  ? $t(`upgrades.${upgrade}.name`)
                  : upgrade
              }}
            </div>
          </td>
          <td>
            <button
              v-if="item.invoiceAvailable"
              class="button-icon"
              type="button"
              @click.prevent="downloadOrderInvoice(item.id)"
            >
              <lucide-icon name="download" />
            </button>
            <template v-else>–</template>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="localOpen && orderHistory.length == 0">
      <loading-spinner v-if="loading" size="small" />
      <span v-else>{{ $t("messages.noOrderHistory") }}</span>
    </p>
  </article>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { get } from "@/api/API";
import { showError } from "@core/ui/alert/alert";
import { formatDate, formatDateStringRelative } from "@/i18n";
import { usePlans } from "@manager/plans/plans.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import LoadingSpinner from "@core/ui/LoadingSpinner.vue";

const props = withDefaults(
  defineProps<{
    open: boolean;
  }>(),
  {
    open: true
  }
);
const emit = defineEmits<{
  (event: "update:open", open: boolean): void;
}>();

const { plans, loadPlans } = usePlans();
const orderHistory = ref<HistoryItem[]>([]);
const loading = ref(false);

async function loadOrderHistory() {
  try {
    loading.value = true;
    orderHistory.value = await get<HistoryItem[]>("/v1/order-history", {
      minTime: 300
    });
  } catch (e) {
    const error = e as Error;
    console.warn(error);
    showError(error);
  } finally {
    loading.value = false;
  }
}

async function downloadOrderInvoice(orderId: number) {
  try {
    const item = orderHistory.value.find((item) => item.id === orderId);
    const pdf = await get(`/v1/order-history/${orderId}/invoice`, {
      headers: {
        accept: "application/octet-stream"
      },
      responseType: "blob"
    });
    const blob = new Blob([pdf], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = `invoice-${item?.created}.pdf`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    const error = e as Error;
    console.warn(error);
    showError(error);
  }
}

function formatDateTime(datetimeString: string) {
  if (!datetimeString) return "";
  const date = new Date(datetimeString);
  return formatDate(date, "f");
}

const localOpen = ref(props.open);

function setOpen(open: boolean) {
  localOpen.value = open;
  emit("update:open", open);
}

watch(
  () => props.open,
  (open) => {
    localOpen.value = open;
  },
  {
    immediate: true
  }
);

watch(localOpen, (open) => {
  if (open) {
    loadPlans();
    loadOrderHistory();
  }
});

type HistoryItem = {
  id: number;
  created: string;
  type: string;
  price: number;
  priceCurrency: string;
  itemType: string | null;
  licenses: number;
  teams: number;
  upgrades: string[];
  invoiceAvailable: boolean;
};
</script>

<style lang="scss">
.order-history {
  &__heading {
    font-size: 1.25rem;
    color: var(--color-text-moderate);
  }

  &__items {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;

    tbody {
      box-shadow: 0 2px 5px var(--color-shadow-1);
      border-radius: 5px;
      background-color: var(--color-background-deep);
    }
  }
  &__header {
    th {
      text-align: left;
      padding: 5px 0.5rem;

      &:first-child {
        padding-left: 1rem;
      }
      &:last-child {
        padding-right: 1rem;
      }
    }
  }
  &__item {
    td {
      padding: 0.5rem 0.5rem;

      &:first-child {
        padding-left: 1rem;
      }
      &:last-child {
        padding-right: 1rem;
      }
    }
    & + & {
      border-top: 1px solid var(--color-border-light);
    }
  }
}
</style>
